import { useEffect, useState } from 'react';
import { CircularProgress, ListItem, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Add, DeleteOutlined, Refresh, Remove } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '@/stores';
import { MoneyValueZeroFormatter } from '@utils/money-value-formatter.ts';
import { CartItemInfo } from '@/modules/external/components/customer/cart/cart-item-info.component.tsx';
import { useUpsertCartItem } from '@/api/carts/carts.hooks.ts';
import { CartItem } from '@/api/carts/carts.types.ts';

export function CartItemListItem(props: { cartItem: CartItem }) {
  const queryClient = useQueryClient();
  const { contactEmployeeId } = useGlobalStore();
  const { mutateAsync: upsertCartItem, isPending: upsertCartItemIsPending } =
    useUpsertCartItem();
  const [quantity, setQuantity] = useState(props.cartItem.quantity);

  // `debouncing` is true whenever the local quantity differs from the persisted
  // cart quantity — i.e. while the debounced update is still pending.
  const debouncing = quantity !== props.cartItem.quantity;

  const updateCartItem = async (quantity: number) => {
    await upsertCartItem({
      cartItems: [
        {
          itemId: props.cartItem.item.id,
          quantity,
        },
      ],
    });
  };

  // TODO: lift this state up and debounce all quantity updates
  // debounce quantity update
  useEffect(() => {
    if (quantity !== props.cartItem.quantity) {
      const timeout = setTimeout(() => {
        updateCartItem(quantity);
      }, 2500);
      return () => clearTimeout(timeout);
    }
  }, [props.cartItem.quantity, quantity]);

  const removeCartItem = async () => {
    await upsertCartItem({
      cartItems: [
        {
          itemId: props.cartItem.item.id,
          quantity: 0,
        },
      ],
    });
    queryClient.invalidateQueries({
      queryKey: ['cart', contactEmployeeId],
    });
  };

  return (
    <ListItem divider={true}>
      <Stack
        direction={{
          xs: 'column',
          // sm: "row",
        }}
        sx={{
          width: '100%',
          alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
        }}
      >
        <CartItemInfo cartItem={props.cartItem} />
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            pl: {
              xs: 0,
              sm: 2,
              md: 4,
            },
            ml: 'auto',
            alignItems: 'center',
            alignSelf: {
              xs: 'end',
              sm: 'center',
            },
          }}
        >
          <Stack
            sx={{
              pr: {
                xs: 2,
                md: 4,
              },
              display: 'flex',
              width: {
                md: '100px',
              },
              alignItems: 'end',
            }}
            direction={'column'}
          >
            <Typography
              sx={{
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
              variant={'subtitle2'}
            >
              {`${MoneyValueZeroFormatter(props.cartItem.item.itemPrice)}`}
            </Typography>
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                alignSelf: 'end',
              }}
              variant={'caption'}
            >
              (netto)
            </Typography>
          </Stack>
          {!props.cartItem.item.itemPrice || quantity === 1 ? null : (
            <Stack
              sx={{
                pr: {
                  xs: 2,
                  md: 4,
                },
                display: 'flex',
                visibility:
                  !props.cartItem.item.itemPrice || quantity === 1
                    ? 'hidden'
                    : 'visible',
                width: {
                  md: '100px',
                },
                alignItems: 'end',
              }}
              direction={'column'}
            >
              <Typography
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
                variant={'subtitle2'}
              >
                {MoneyValueZeroFormatter(
                  (props.cartItem.item.itemPrice || 0) * quantity,
                )}
              </Typography>
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  alignSelf: 'end',
                }}
                variant={'caption'}
              >
                (netto)
              </Typography>
            </Stack>
          )}
          <Stack
            sx={{
              height: '30px',
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
              flexGrow: 0,
              backgroundColor: '#e1e1e1',
              borderRadius: '4px',
              my: 'auto',
            }}
            direction={'row'}
          >
            <IconButton
              size={'small'}
              onClick={() => setQuantity((prev) => (prev > 0 ? prev - 1 : 0))}
              sx={{
                py: 0,
              }}
            >
              <Remove sx={{ height: '20px', width: '20px' }} />
            </IconButton>
            <input
              className={'no-arrows'}
              style={{
                all: 'unset',
                flexGrow: 1,
                minWidth: '25px',
                maxWidth: '36px',
                textAlign: 'center',
                backgroundColor: '#f7f7f7',
                height: '100%',
              }}
              maxLength={4}
              inputMode={'numeric'}
              value={quantity || 0}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
              onBlur={(e) => {
                const value = parseInt(e.target.value);
                if (isNaN(value)) {
                  setQuantity(1);
                  return;
                }
                if (value < 0) {
                  setQuantity(1);
                  return;
                }
                setQuantity(value);
              }}
            />
            <IconButton
              size={'small'}
              onClick={() => setQuantity((prev) => prev + 1)}
              sx={{ py: 0 }}
            >
              <Add sx={{ height: '20px', width: '20px' }} />
            </IconButton>
          </Stack>
          <Stack sx={{ ml: 1 }} direction={'row'}>
            <IconButton color={'inherit'} disabled={true}>
              {debouncing || upsertCartItemIsPending ? (
                <CircularProgress
                  style={{ height: '24px', width: '24px' }}
                  sx={{ height: '24px', width: '24px' }}
                />
              ) : (
                <Refresh style={{ height: '24px', width: '24px' }} />
              )}
            </IconButton>
            <Tooltip
              title={'Aus Warenkorb entfernen'}
              disableInteractive
              disableFocusListener
              disableTouchListener
              arrow
            >
              <IconButton
                color={'inherit'}
                onClick={() => removeCartItem()}
                disabled={debouncing || upsertCartItemIsPending}
              >
                <DeleteOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Stack>
    </ListItem>
  );
}
